
import { useRef } from "react";
import { Link } from "react-router";
import { useLocation, useNavigate } from "react-router";
import { Home, Plus, Camera, Receipt, TrendingUp, PieChart } from "lucide-react";
import { cn } from "@/lib/utils";
import { setPendingCapture } from "@/lib/pending-capture";

export function BottomNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const cameraRef = useRef<HTMLInputElement>(null);

  function onCapture(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPendingCapture(file);
    navigate("/capture");
  }

  const tab = (active: boolean) =>
    cn(
      "flex flex-1 flex-col items-center justify-center gap-0.5 rounded-3xl text-[10px] font-medium transition-colors active:bg-black/[0.04]",
      active ? "text-[#F0492F]" : "text-muted-foreground",
    );

  return (
    <nav className="pointer-events-none fixed inset-x-0 bottom-0 z-40 lg:hidden">
      <div className="mx-auto max-w-md px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="pointer-events-auto flex h-18 items-stretch gap-0.5 rounded-[1.75rem] bg-white/55 px-3 shadow-[0_8px_30px_rgba(0,0,0,0.12)] ring-1 ring-white/40 backdrop-blur-2xl supports-[backdrop-filter]:bg-white/45">
          <Link to="/" aria-label="Home" className={tab(pathname === "/")}>
            <Home className="size-5.5" strokeWidth={pathname === "/" ? 2.4 : 2} />
            Home
          </Link>

          <Link
            to="/transactions"
            aria-label="Transactions"
            className={tab(pathname === "/transactions")}
          >
            <Receipt
              className="size-5.5"
              strokeWidth={pathname === "/transactions" ? 2.4 : 2}
            />
            Activity
          </Link>

          <Link
            to="/earnings"
            aria-label="Earnings"
            className={tab(pathname.startsWith("/earnings"))}
          >
            <TrendingUp
              className="size-5.5"
              strokeWidth={pathname.startsWith("/earnings") ? 2.4 : 2}
            />
            Income
          </Link>

          <Link
            to="/analytics"
            aria-label="Analytics"
            className={tab(pathname === "/analytics")}
          >
            <PieChart
              className="size-5.5"
              strokeWidth={pathname === "/analytics" ? 2.4 : 2}
            />
            Stats
          </Link>

          {/* Opens the camera directly from the tap (iOS needs the real
              gesture); the photo is handed to /capture to read. */}
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={onCapture}
          />
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            aria-label="Scan receipt"
            className={tab(pathname === "/capture")}
          >
            <Camera
              className="size-5.5"
              strokeWidth={pathname === "/capture" ? 2.4 : 2}
            />
            Scan
          </button>

          {/* Fast manual entry — prominent, one tap. */}
          <Link
            to="/add"
            aria-label="Add expense"
            className="flex items-center pl-1"
          >
            <span className="flex size-13 items-center justify-center rounded-full bg-gradient-to-br from-[#FF8A5B] to-[#F0492F] text-white shadow-lg shadow-orange-500/35 ring-1 ring-white/30 transition-transform active:scale-95">
              <Plus className="size-6" strokeWidth={2.5} />
            </span>
          </Link>
        </div>
      </div>
    </nav>
  );
}
